/**
 *
 * Displays a paginated, searchable, and filterable list of item templates.
 * Features:
 *   - Search by description
 *   - Category filtering
 *   - Pagination controls
 *   - Edit template via modal
 *   - Delete template with confirmation dialog
 *   - View moved or hidden/inactive materials via icons
 *
 * State Variables:
 *   - search: current search term
 *   - offset: pagination offset
 *   - state.data: list of templates
 *   - state.currentPage: current page number
 *   - state.limit: items per page
 *   - state.deleteId: ID of template to delete
 *   - state.editItem: { open, id } for editing
 *   - category: selected category filter
 *   - viewCategoryDialogState: toggle for category dialog
 *   - showMovedModal: toggle for moved/hidden materials modal
 *   - modalMaterials: data for modal contents
 *   - modalType: 'moved' or 'hidden' modal type
 *
 * @helper fetchItemTemplates: loads templates from server
 * @helper updateSearch: updates search and resets offset
 * @helper updateOffset: sets pagination offset
 * @helper editHandler: opens edit modal
 * @helper deleteHandler: deletes template and refreshes list
 * @helper openMaterialModal: opens modal for moved/hidden materials
 */
import "../projects/scss/task.scss";
import React, { useEffect, useState, useCallback } from "react";
import "../projects/scss/task.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteItemTemplate,
  getItemTemplates,
} from "../../actions/template-management.action";
import { toUSD } from "../../util/currency-formatter";
import CustomTable from "../../components/table/table";
import EditTemplate from "./edit-templates";
import DeleteDialog from "../../components/delete-dialog";
import { clearTaskAction } from "../../slice/add-task";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TemplateCategory from "./category.jsx";
import { listTemplateCategories } from "../../actions/inventory.action";
import Cookies from "js-cookie";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Tooltip } from "@mui/material";
import MovedMaterialsModal from './movedMaterialsModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { errorSnackBar } from '../../actions/common.action';
import MaterialIssuesFilter from '../../components/material-issues-filter';
import { roundValue, toDecimal } from "../../util/decimal-utils";

function ItemTemplateList(props) {
    const dispatch = useDispatch();
    const { hideMaterialIssues = false } = props;
    
    const categoryList = useSelector(state => state.category.categories) || [];
    const [search, setSearch] = useState('');
    const [viewCategoryDialogState, setViewCategoryDialogState] = useState(false);
    const [state, setState] = useState({
        limit: 25,
        totalCount: 20,
        currentPage: 1,
        data: [],
        editItem: { open: false, id: '' },
        deleteId: '',
    });
    const [offset, setOffset] = useState(0);
    const [category, setCategory] = useState([]);
    const [materialIssuesFilter, setMaterialIssuesFilter] = useState('');
    const [showMovedModal, setShowMovedModal] = useState(false);
    const [modalTemplateId, setModalTemplateId] = useState(null);
    const [modalMaterials, setModalMaterials] = useState([]);
    const [modalType, setModalType] = useState('');

    const fetchItemTemplates = useCallback(() => {
        const recordsPerPage = Cookies.get("recordsPerPage") ? parseInt(Cookies.get("recordsPerPage")) : 25;
        const requestParams = { search, offset, limit: recordsPerPage, category };
        
        // Only include materialIssues filter when not hidden
        if (!hideMaterialIssues) {
            requestParams.materialIssues = materialIssuesFilter;
        }
        
        
        
        dispatch(getItemTemplates(requestParams))
            .then(response => {
                setState(prevState => ({
                    ...prevState,
                    data: response.items || [],
                    totalCount: response.totalCount || 0,
                    limit: response.limit || prevState.limit,
                    offset: response.offset || prevState.offset,
                    currentPage: response.currentPage || prevState.currentPage,
                }));
            })
            .catch((error) => {
                dispatch(errorSnackBar("Failed to fetch item templates. Please try again later."));
            });
    }, [search, offset, state.limit, category, materialIssuesFilter, hideMaterialIssues, dispatch]);

    useEffect(() => {
        fetchItemTemplates();
    }, [fetchItemTemplates]);

    useEffect(() => {
        if (categoryList.length === 0) {
            dispatch(listTemplateCategories()).catch(() => {
                dispatch(errorSnackBar("Failed to load template categories. Please try again later."));
            });
        }
    }, []);

    useEffect(() => {
        const handleRefreshEvent = (event) => {
            fetchItemTemplates();
        };

        window.addEventListener('refreshItemTemplateList', handleRefreshEvent);

        return () => {
            window.removeEventListener('refreshItemTemplateList', handleRefreshEvent);
        };
    }, [fetchItemTemplates]);


    const updateSearch = (value) => {
        if (value == null) return;
        const trimmedVal = String(value).trimStart();
        if (trimmedVal === search) {
          return;
        }
        setSearch(trimmedVal);
        // Only reset materialIssuesFilter when not hidden
        if (!hideMaterialIssues) {
            setMaterialIssuesFilter('');
        }
        setOffset(0);
        setState(prev => ({
          ...prev,
          currentPage: 1
        }));
    };

    const updateOffset = (pageNumber, limit) => {
        const validPageNumber = pageNumber > 0 ? pageNumber : 1;
        if (!limit) return;
        const _offset = (validPageNumber - 1) * limit;
        setOffset(_offset);
        
        setState(prevState => ({
            ...prevState,
            currentPage: validPageNumber
        }));
    };

    const handleMaterialIssuesFilterChange = (filterValue) => {
        // Only handle filter changes when not hidden
        if (!hideMaterialIssues) {
            setMaterialIssuesFilter(filterValue);
            setOffset(0);
            setState(prevState => ({
                ...prevState,
                currentPage: 1
            }));
        }
    };

    const editHandler = (id) => {
        dispatch(clearTaskAction());
        setState(prev => ({
            ...prev,
            editItem: {
                open: !prev.editItem.open,
                id: id
            }
        }));
    };

    const deleteDialogHandler = (id = '') => {
        setState(prev => ({
            ...prev,
            deleteId: id
        }));
    };

    const deleteHandler = (id) => {
        dispatch(deleteItemTemplate(id)).then(() => {
            deleteDialogHandler();
            setCategory([]);
            // Only reset materialIssuesFilter when not hidden
            if (!hideMaterialIssues) {
                setMaterialIssuesFilter('');
            }
            setOffset(0);
            dispatch(listTemplateCategories()).catch(e => console.error(e));
            fetchItemTemplates();
        }).catch(e => console.error(e));
    };

    const handleLimitChange = (newLimit) => {
        setState(prevState => ({
            ...prevState,
            limit: newLimit,
        }));
        setOffset(0);
    };

    const viewCategoryDialogHandler = () => {
        setViewCategoryDialogState(!viewCategoryDialogState);
    };

    const onCategoryChangeHandler = (selectedCategoryValues) => {
        setCategory(selectedCategoryValues);
        // Only reset materialIssuesFilter when not hidden
        if (!hideMaterialIssues) {
            setMaterialIssuesFilter('');
        }
       //setOffset(0);
    };

    const openMaterialModal = (rowData, type) => {
        if (type === 'moved') {
            setModalMaterials(rowData.movedMaterials);
        } else if (type === 'all') {
            setModalMaterials({
                hiddenMaterials: rowData.hiddenMaterials || [],
                inactiveMaterials: rowData.inactiveMaterials || [],
                differentAccountMaterials: rowData.differentAccountMaterials || [],
                duplicateMaterials: rowData.duplicateMaterials || []
            });
        } else if (type === 'hidden' || type === 'differentAccount') {
            setModalMaterials({
                hiddenMaterials: rowData.hiddenMaterials || [],
                inactiveMaterials: rowData.inactiveMaterials || [],
                differentAccountMaterials: rowData.differentAccountMaterials || []
            });
        } else if (type === 'duplicate') {
            setModalMaterials({
                duplicateMaterials: rowData.duplicateMaterials || []
            });
        }
        setModalType(type);
        setModalTemplateId(rowData.id);
        setShowMovedModal(true);
    };

    const deleteDescription = () => {
        const itemTemplate = state.data.find((item) => item.id === state.deleteId);
        const itemDescription = itemTemplate ? itemTemplate.description : "";
        return `Item Template - ${itemDescription}`;
    };

    const columns = [
        {
            title: "Description",
            field: "description",
            class: "w-auto fw-bold",
            render: (rowData) => (
                <div className="d-flex align-items-center gap-2">
                    {rowData.description}
                    {!hideMaterialIssues && rowData.hasMovedMaterials && (
                        <Tooltip title="This template contains moved materials" placement="left" arrow followCursor={true}>
                            <InfoOutlinedIcon
                                data-cy="Moved Materials Icon"
                                className="text-info cursor-pointer shadow-sm transition hover-translate"
                                onClick={() => openMaterialModal(rowData, 'moved')}
                            />
                        </Tooltip>
                    )}
                    {!hideMaterialIssues && (rowData.hasHiddenMaterials || rowData.hasInactiveMaterials || rowData.hasDifferentAccountMaterials || rowData.hasDuplicateMaterials) && (
                        <Tooltip
                            title={
                                rowData.hasDifferentAccountMaterials
                                    ? "This template contains materials from a different user account"
                                    : rowData.hasDuplicateMaterials && (rowData.hasHiddenMaterials || rowData.hasInactiveMaterials)
                                    ? "This template contains broken and duplicate materials"
                                    : rowData.hasDuplicateMaterials
                                    ? "This template contains materials with duplicates"
                                    : "This template contains hidden/inactive materials"
                            }
                            placement="left"
                            arrow
                            followCursor={true}
                        >
                            <WarningAmberIcon
                                data-cy="Warning Icon"
                                className={
                                    rowData.hasDifferentAccountMaterials
                                        ? "text-danger cursor-pointer shadow-sm transition hover-translate"
                                        : "text-warning cursor-pointer shadow-sm transition hover-translate"
                                }
                                onClick={() => openMaterialModal(rowData, 'all')}
                            />
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: "Category",
            field: "category_id",
            class: 'fw-bold text-right',
            render: (rowData) => <>{rowData.category?.name ?? ''}</>
        },
        {
            title: "Material",
            field: "material_total_cost",
            class: 'fw-bold text-right',
            render: (rowData) => <>{toUSD(rowData.material_total_cost)}</>
        },
        {
            title: "Resource",
            field: "labor_total_cost",
            class: 'fw-bold text-right',
            render: (rowData) => <>{toUSD(rowData.labor_total_cost)}</>
        },
        {
            title: "Contractor",
            field: "subcontractor_total_cost",
            class: 'fw-bold text-right',
            render: (rowData) => <>{toUSD(rowData.subcontractor_total_cost)}</>
        },
        {
            title: "Total",
            field: "total",
            class: 'fw-bold text-right',
            render: (rowData) => (
                <>
                    {toUSD(
                        roundValue(
                            toDecimal(rowData.material_total_cost)
                                .plus(toDecimal(rowData.labor_total_cost))
                                .plus(toDecimal(rowData.subcontractor_total_cost))
                        )
                    )}
                </>
            ),
        },
        {
            title: "Action", 
            field: "action", 
            class: 'fw-bold text-center',
            render: (rowData) => (
                <div className="d-flex gap-2 justify-content-center">
                    <div className="action-edit">
                        <EditIcon onClick={() => editHandler(rowData.id)} data-cy="edit-item-template" />
                    </div>
                    <div className="action-edit">
                        <DeleteIcon className='deleteicon' onClick={() => deleteDialogHandler(rowData.id)} />
                    </div>
                </div>
            )
        },
    ];

    return (
        <div className="item-template-list">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="content-heading text-uppercase">Item Template</h2>
                <div className="d-flex align-items-center">
                    {!hideMaterialIssues && (
                        <MaterialIssuesFilter
                            selectedFilter={materialIssuesFilter}
                            onFilterChange={handleMaterialIssuesFilterChange}
                        />
                    )}
                    <button
                        onClick={viewCategoryDialogHandler}
                        className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                    >
                        View Category
                    </button>
                </div>
            </div>
            <CustomTable
                key={`${!hideMaterialIssues ? materialIssuesFilter : ''}-${category.join(',')}`}
                columns={columns}
                data={state.data}
                paginationCount={state.totalCount}
                pageHandler={updateOffset}
                itemsPerPage={state.limit}
                fileName='Item Template List'
                searchHandler={updateSearch}
                limitChangeHandler={handleLimitChange}
                currentPage={state.currentPage}
                categoryFilterProps={{
                    categories: categoryList,
                    selectedCategories: category,
                    onCategoryChange: onCategoryChangeHandler,
                    label: 'Category Filter',
                }}
            />
            <DeleteDialog
                open={state.deleteId}
                id={state.deleteId}
                deleteHandler={deleteHandler}
                dialogClose={() => deleteDialogHandler()}
                moduleName="Item Template"
                description={deleteDescription()}
            />
            {state.editItem.open && <EditTemplate
                open={state.editItem.open}
                editData={state.data}
                dialogClose={() => editHandler('')}
                isSubTask={true}
                subTaskId={state.editItem.id}
                saveHandler={() => {
                    fetchItemTemplates();
                    editHandler('');
                }}
                onRefreshTemplateList={fetchItemTemplates}
            />}
            {viewCategoryDialogState && (
                <TemplateCategory
                    open={viewCategoryDialogState}
                    showList={true}
                    dialogClose={viewCategoryDialogHandler}
                    fetchItemTemplates={fetchItemTemplates}
                    resetSelectedCategories={() => setCategory([])}
                />
            )}
            {showMovedModal && !hideMaterialIssues && (
                <MovedMaterialsModal
                    show={showMovedModal}
                    onClose={() => setShowMovedModal(false)}
                    materials={modalMaterials}
                    type={modalType}
                    templateId={modalTemplateId}
                    onEditTemplate={() => {
                        setShowMovedModal(false);
                        setTimeout(() => editHandler(modalTemplateId), 300);
                    }}
                />
            )}
        </div>
    );
}

export default ItemTemplateList;
